import { Link } from "react-router-dom";
import { SectionHeading } from "@/components/site/SectionHeading";
import { SafeImage } from "@/components/site/SafeImage";
import { INSIGHTS, formatInsightDate } from "@/lib/insights";

/**
 * Homepage "Insights" preview — three most-recent articles drawn from
 * the editorial register in `src/lib/insights.ts`.
 */
export function InsightsPreview() {
  const recent = [...INSIGHTS]
    .sort((a, b) => (a.date < b.date ? 1 : -1))
    .slice(0, 3);

  return (
    <section className="border-t border-[var(--line)] bg-[var(--bg-2)]">
      <div className="container-wide py-14 sm:py-18 md:py-24 short:py-10">
        <SectionHeading
          eyebrow="Insights"
          size="xl"
          title="Dubai Market Intelligence."
          body="Market reports, neighbourhood guides, and investor briefings — everything you need to make confident property decisions in Dubai."
          aside={
            <Link to="/insights" className="link-arrow">
              View All Articles
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path
                  d="M2 7h10m-4-4 4 4-4 4"
                  stroke="currentColor"
                  strokeWidth="1.2"
                />
              </svg>
            </Link>
          }
        />

        <ul className="mt-10 grid gap-6 sm:mt-12 md:grid-cols-3">
          {recent.map((a) => (
            <li
              key={a.slug}
              className="group flex flex-col overflow-hidden border border-[var(--line-strong)] bg-[var(--surface)] transition-all hover:border-[var(--gold-bright)]"
            >
              <Link
                to={`/insights/${a.slug}`}
                className="flex flex-1 flex-col"
              >
                <div className="relative aspect-[16/10] overflow-hidden bg-[var(--surface-2)]">
                  <SafeImage
                    src={a.image}
                    alt={a.title}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
                  />
                </div>
                {/* `flex-1`, not `h-full`: a percentage height here resolves
                    against the whole link (image included) and inflates the
                    grid row on single-column phone layouts. */}
                <div className="flex flex-1 flex-col gap-4 p-6 sm:p-7">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold-bright)]">
                    {a.category}
                  </p>
                  <h3
                    className="font-display text-[20px] leading-tight text-[var(--text)] transition-colors group-hover:text-[var(--gold-bright)] sm:text-[24px]"
                    style={{ fontVariationSettings: '"opsz" 36' }}
                  >
                    {a.title}
                  </h3>
                  <p className="text-[14px] leading-[1.65] text-[var(--text-soft)]">
                    {a.excerpt}
                  </p>
                  <div className="mt-auto pt-4 text-[12px] uppercase tracking-[0.16em] text-[var(--text-muted)]">
                    {formatInsightDate(a.date)} · {a.readMinutes} min read
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
